import MailOutlineIcon from "@mui/icons-material/MailOutline";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Link,
  TextField,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import type { FormEvent } from "react";
import { authFlowHeaderIcons } from "./authFlowHeaderIcons";
import {
  getLoginFieldSx,
  getLoginFooterLinkBoxSx,
  getLoginPrimaryButtonSx,
  getLoginSecondaryLinkSx,
  LoginCardContent,
  LoginFormHeader,
} from "./loginUi";

export type ForgotPasswordFormProps = {
  onSubmit?: (email: string) => void;
  error?: string;
  loading?: boolean;
  initialEmail?: string;
  loginUrl?: string;
  translations: {
    resetPassword: string;
    resetPasswordInstructions: string;
    email: string;
    emailPlaceholder: string;
    sendResetLink: string;
    backToLogin: string;
    fieldRequired: string;
    invalidEmail: string;
  };
};

export const ForgotPasswordForm = ({
  onSubmit,
  error,
  loading = false,
  initialEmail = "",
  loginUrl = "/accounts/login/",
  translations: t,
}: ForgotPasswordFormProps) => {
  const theme = useTheme();
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState("");

  const validate = (): boolean => {
    setEmailError("");

    if (!email.trim()) {
      setEmailError(t.fieldRequired);
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t.invalidEmail);
      return false;
    }

    return true;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit?.(email);
    }
  };

  const fieldSx = getLoginFieldSx(theme, 3);

  return (
    <LoginCardContent
      component="form"
      onSubmit={handleSubmit}
      testId="forgot-password-form"
    >
      <LoginFormHeader
        title={t.resetPassword}
        subtitle={t.resetPasswordInstructions}
        headerIcon={authFlowHeaderIcons.forgotPassword}
        headerTestId="forgot-password-form-header"
        titleTestId="forgot-password-form-title"
        headerIconTestId="forgot-password-form-header-icon"
      />

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2.5 }}
          data-testid="forgot-password-error"
        >
          {error}
        </Alert>
      ) : null}

      <TextField
        fullWidth
        label={t.email}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!emailError}
        helperText={emailError}
        placeholder={t.emailPlaceholder}
        autoComplete="email"
        autoFocus
        sx={fieldSx}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <MailOutlineIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            inputProps: { "data-testid": "forgot-password-email" },
          },
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        data-testid="forgot-password-submit"
        sx={getLoginPrimaryButtonSx(theme)}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          t.sendResetLink
        )}
      </Button>

      <Box sx={getLoginFooterLinkBoxSx()}>
        <Link
          href={loginUrl}
          underline="hover"
          color="text.secondary"
          sx={getLoginSecondaryLinkSx()}
          data-testid="back-to-login-link"
        >
          {t.backToLogin}
        </Link>
      </Box>
    </LoginCardContent>
  );
};
